import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award, ArrowRight, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const CoachRegistrationPage = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    city: '',
    area: '',
    club: '',
    specialties: [],
    experience_years: 0,
    price_private: '',
    price_duo: '',
    price_group: '',
    description: '',
    certifications: '',
    availability: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    image: '',
  });

  const areaOptions = ['צפון', 'שרון', 'מרכז', 'ירושלים', 'דרום'];
  const specialtyOptions = ['ילדים', 'נוער', 'מתחילים', 'מתקדמים', 'מבוגרים', 'תחרותי'];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialty = (spec) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(spec)
        ? prev.specialties.filter(s => s !== spec)
        : [...prev.specialties, spec]
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('התמונה גדולה מדי (מקסימום 5MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('עליך להיות מחובר כדי להירשם כמאמן');
      navigate('/auth');
      return;
    }
    if (!formData.full_name || !formData.phone || !formData.city || !formData.area) {
      toast.error('אנא מלא את כל השדות החובה');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/coaches/register`, formData);
      setSubmitted(true);
      toast.success('הבקשה נשלחה בהצלחה!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בשליחת הבקשה');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <Award className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-3">הירשם כמאמן</h2>
          <p className="text-gray-600 mb-6">עליך להיות מחובר כדי להירשם כמאמן באתר</p>
          <Link to="/auth">
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold px-8 py-3">
              התחבר / הירשם
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="w-14 h-14 text-emerald-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-3" data-testid="success-title">הבקשה נשלחה בהצלחה!</h2>
          <p className="text-gray-600 text-lg mb-8">
            הפרופיל שלך יופיע באתר לאחר אישור מנהל המערכת. זה בדרך כלל לוקח עד 24 שעות.
          </p>
          <Link to="/coaches">
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold px-8 py-3 rounded-xl">
              חזרה לדף המאמנים
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link to="/coaches" className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors mb-6 group">
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          <span className="font-medium">חזרה לרשימת המאמנים</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-lg">
            <Award className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">הירשם כמאמן</h1>
          <p className="text-gray-600">מלא את הפרטים ותופיע ברשימת המאמנים של Tennis Pro Israel</p>
        </div>

        <Card className="border-2 border-gray-100 shadow-xl overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500" />
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div className="text-center">
                <div className="relative inline-block">
                  {formData.image ? (
                    <img src={formData.image} alt="Coach" className="w-28 h-28 rounded-full object-cover ring-4 ring-emerald-200 shadow-lg" />
                  ) : (
                    <div className="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center ring-4 ring-gray-200">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    data-testid="image-upload"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">לחץ להעלאת תמונה (עד 5MB)</p>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold text-gray-700">שם מלא <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    placeholder="השם המלא שלך"
                    className="mt-1"
                    data-testid="input-full-name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700">טלפון <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="050-0000000"
                    className="mt-1"
                    data-testid="input-phone"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-bold text-gray-700">אימייל</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@example.com"
                  className="mt-1"
                  data-testid="input-email"
                />
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold text-gray-700">עיר <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="שם העיר"
                    className="mt-1"
                    data-testid="input-city"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700">אזור <span className="text-red-500">*</span></Label>
                  <select
                    value={formData.area}
                    onChange={(e) => handleChange('area', e.target.value)}
                    className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                    data-testid="select-area"
                    required
                  >
                    <option value="">בחר אזור</option>
                    {areaOptions.map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Club */}
              <div>
                <Label className="text-sm font-bold text-gray-700">מועדון / מרכז טניס</Label>
                <Input
                  value={formData.club}
                  onChange={(e) => handleChange('club', e.target.value)}
                  placeholder='שם המועדון בו אתה מאמן (לדוגמא: מרכז הטניס רעננה)'
                  className="mt-1"
                  data-testid="input-club"
                />
              </div>

              {/* Specialties */}
              <div>
                <Label className="text-sm font-bold text-gray-700 mb-2 block">התמחויות</Label>
                <div className="flex flex-wrap gap-2">
                  {specialtyOptions.map(spec => (
                    <button
                      key={spec}
                      type="button"
                      onClick={() => toggleSpecialty(spec)}
                      data-testid={`specialty-${spec}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        formData.specialties.includes(spec)
                          ? 'bg-emerald-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      {spec}
                    </button>
                  ))}
                </div>
              </div>

              {/* Experience */}
              <div>
                <Label className="text-sm font-bold text-gray-700">שנות ניסיון</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.experience_years}
                  onChange={(e) => handleChange('experience_years', parseInt(e.target.value) || 0)}
                  className="mt-1"
                  data-testid="input-experience"
                />
              </div>

              {/* Prices */}
              <div>
                <Label className="text-sm font-bold text-gray-700 mb-2 block">מחירון</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">אימון פרטי</Label>
                    <Input
                      value={formData.price_private}
                      onChange={(e) => handleChange('price_private', e.target.value)}
                      placeholder='200 ש"ח'
                      className="mt-1"
                      data-testid="input-price-private"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">אימון זוגי</Label>
                    <Input
                      value={formData.price_duo}
                      onChange={(e) => handleChange('price_duo', e.target.value)}
                      placeholder='150 ש"ח לאדם'
                      className="mt-1"
                      data-testid="input-price-duo"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">אימון קבוצתי</Label>
                    <Input
                      value={formData.price_group}
                      onChange={(e) => handleChange('price_group', e.target.value)}
                      placeholder='100 ש"ח לאדם'
                      className="mt-1"
                      data-testid="input-price-group"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-bold text-gray-700">תיאור</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="ספר על עצמך, הניסיון שלך, סגנון האימון..."
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[100px] focus:border-emerald-400 focus:outline-none resize-none"
                  data-testid="input-description"
                />
              </div>

              {/* Certifications & Availability */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold text-gray-700">הסמכות</Label>
                  <Input
                    value={formData.certifications}
                    onChange={(e) => handleChange('certifications', e.target.value)}
                    placeholder="הסמכות מקצועיות"
                    className="mt-1"
                    data-testid="input-certifications"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700">זמינות</Label>
                  <Input
                    value={formData.availability}
                    onChange={(e) => handleChange('availability', e.target.value)}
                    placeholder='ימים א-ה, בוקר/ערב'
                    className="mt-1"
                    data-testid="input-availability"
                  />
                </div>
              </div>

              {/* Social Media */}
              <div>
                <Label className="text-sm font-bold text-gray-700 mb-2 block">רשתות חברתיות</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input
                    value={formData.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    placeholder="WhatsApp (מספר)"
                    className="text-sm"
                    data-testid="input-whatsapp"
                  />
                  <Input
                    value={formData.instagram}
                    onChange={(e) => handleChange('instagram', e.target.value)}
                    placeholder="Instagram"
                    className="text-sm"
                    data-testid="input-instagram"
                  />
                  <Input
                    value={formData.facebook}
                    onChange={(e) => handleChange('facebook', e.target.value)}
                    placeholder="Facebook"
                    className="text-sm"
                    data-testid="input-facebook"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                data-testid="submit-coach-btn"
              >
                {submitting ? 'שולח...' : 'שלח בקשת הרשמה'}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                הפרופיל שלך יופיע באתר לאחר אישור מנהל המערכת
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CoachRegistrationPage;
